import { useState, useRef } from "react";
import { mutate } from "swr";
import {
    Button,
    Card,
    CardBody,
    CardFooter,
    CardHeader,
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
    Input,
    IconButton,
    Typography,
} from "@material-tailwind/react";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { fetcherSimple, fetcherFull, fetcherFileUpload } from "utils/api";
import { reportError } from "utils/api-error";
import FormikSwitch from "components/form/formik-switch";
import MaterialIcon from "components/material-icon";
import "react-toastify/dist/ReactToastify.css";
import * as yup from "yup";

export default function PeopleContent({
    survey,
    surveyId,
    surveyRecipients,
    surveyRecipientsError,
    recipientsMutate,
}) {
    const inputTextRef = useRef(null);
    const inputCsvFileRef = useRef(null);

    const addRecipient = async (validationSchemaRecipientEmail) => {
        if (
            survey.state !== "draft" &&
            survey.state !== "published" &&
            survey.state !== "paused"
        ) {
            toast.warn(
                "Survey has already been started and can no longer be edited!",
            );
            return;
        }
        const value = inputTextRef?.current?.value;
        if (!value) {
            toast.warn("Email for Respondent missing!");
            return;
        }

        try {
            await validationSchemaRecipientEmail.validate({ recipient: value });

            const submitToast = toast.loading("Saving Recipient...");
            const uidStr = encodeURI(inputTextRef.current.value);
            const response = await fetcherFull(
                `/v2/survey/${surveyId}/recipient`,
                "POST",
                { uid: uidStr },
            );
            if (response && response.id) {
                toast.update(submitToast, {
                    render: "Recipient Uploaded",
                    type: "success",
                    isLoading: false,
                    autoClose: 5000,
                    pauseOnFocusLoss: false,
                    pauseOnHover: false,
                    closeOnClick: true,
                    closeButton: true,
                });
                recipientsMutate(surveyRecipients);
                inputTextRef.current.value = "";
            } else {
                toast.update(submitToast, {
                    render: "Error uploading Recipient",
                    type: "error",
                    isLoading: false,
                    autoClose: 5000,
                    pauseOnFocusLoss: false,
                    pauseOnHover: false,
                    closeOnClick: true,
                    closeButton: true,
                });
            }
        } catch (err) {
            toast.error(err.message || "Invalid recipient");
        }
    };

    const deleteRecipient = async (recipientId) => {
        if (
            survey.state !== "draft" &&
            survey.state !== "published" &&
            survey.state !== "paused"
        ) {
            toast.warn(
                "Survey has already been started and can no longer be edited!",
            );
            return;
        }
        const submitToast = toast.loading("Deleting Recipient...");
        const response = await fetcherSimple(
            `/v2/survey/${surveyId}/recipient/${recipientId}`,
            "DELETE",
        );
        if (response && response.success) {
            toast.update(submitToast, {
                render: "Recipient Deleted",
                type: "success",
                isLoading: false,
                autoClose: 5000,
                pauseOnFocusLoss: false,
                pauseOnHover: false,
                closeOnClick: true,
                closeButton: true,
            });
            recipientsMutate(surveyRecipients);
            inputTextRef.current.value = "";
        } else {
            toast.update(submitToast, {
                render: "Error deleting Recipient",
                type: "error",
                isLoading: false,
                autoClose: 5000,
                pauseOnFocusLoss: false,
                pauseOnHover: false,
                closeOnClick: true,
                closeButton: true,
            });
        }
    };
    const validationSchemaRecipientEmail = yup.object().shape({
        recipient: yup
            .string()
            .test("emailOrId", "Invalid email format", (value) => {
                if (!value) return false;
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                return !value.includes("@") || emailRegex.test(value);
            }),
    });

    const createRecipientLink = async (recipientId) => {
        if (survey.state === "finished") {
            toast.warn("Survey is finished!");
            return;
        }
        const submitToast = toast.loading("Generating Recipient Invite URL...");
        const response = await fetcherSimple(
            `/v2/survey/${surveyId}/recipient/${recipientId}/invitation`,
            "GET",
        );
        if (response && response.url) {
            navigator.clipboard.writeText(response.url);
            toast.update(submitToast, {
                render: "Link for Recipient copied to Clipboard",
                type: "success",
                isLoading: false,
                autoClose: 5000,
                pauseOnFocusLoss: false,
                pauseOnHover: false,
                closeOnClick: true,
                closeButton: true,
            });
            recipientsMutate(surveyRecipients);
            inputTextRef.current.value = "";
        } else {
            toast.update(submitToast, {
                render: "Error creating Link",
                type: "error",
                isLoading: false,
                autoClose: 5000,
                pauseOnFocusLoss: false,
                pauseOnHover: false,
                closeOnClick: true,
                closeButton: true,
            });
        }
    };

    const createInvitationCSV = async () => {
        if (survey.state === "finished") {
            toast.warn("Survey is finished!");
            return;
        }
        const submitToast = toast.loading(
            "Generating Recipient Invitation CSV...",
        );
        const response = await fetcherSimple(
            `/v2/survey/${surveyId}/recipient/invitation`,
            "GET",
        );
        if (response) {
            const inviteList = response;
            if (inviteList.length > 0) {
                const titleKeys = ["User ID", "Invite Link", "Recipient UID", "Respondent UID"];
                const refinedData = [];
                refinedData.push(titleKeys);
                inviteList.forEach((item) => {
                    refinedData.push([
                        item.uid.replaceAll(",", ""),
                        item.url.replaceAll(",", ""),
                        item.recipient_uid ?? "",
                        item.respondent_uid ?? "",
                    ]);
                });
                let csvContent = "";
                refinedData.forEach((row) => {
                    csvContent += row.join(",") + "\n";
                });
                const blob = new Blob([csvContent], {
                    type: "text/csv;charset=utf-8,",
                });
                const objUrl = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.setAttribute("href", objUrl);
                link.setAttribute(
                    "download",
                    `${survey.name}_invitationlist_${dayjs().format(
                        "YYYYMMDD-HHmm",
                    )}.csv`,
                );
                link.click();
            } else {
                toast.warn("No recipients to invite!");
                return;
            }
            toast.update(submitToast, {
                render: "CSV created",
                type: "success",
                isLoading: false,
                autoClose: 5000,
                pauseOnFocusLoss: false,
                pauseOnHover: false,
                closeOnClick: true,
                closeButton: true,
            });
        } else {
            toast.update(submitToast, {
                render: "Error creating Invitation CSV",
                type: "error",
                isLoading: false,
                autoClose: 5000,
                pauseOnFocusLoss: false,
                pauseOnHover: false,
                closeOnClick: true,
                closeButton: true,
            });
        }
    };

    const handleCsvUploadClick = () => {
        inputCsvFileRef.current?.click();
    };

    const csvUploadHandler = async (event) => {
        if (
            survey.state !== "draft" &&
            survey.state !== "published" &&
            survey.state !== "paused"
        ) {
            toast.warn(
                "Survey has already been started and can no longer be edited!",
            );
            return;
        }
        const csvSubmitToast = toast.loading("Saving CSV...");
        if (!event.target.files) {
            toast.error("No file uploaded!");
            return;
        }
        const selectedFile = event.target.files[0];
        let fileResponse = undefined;
        try {
            fileResponse = await fetcherFileUpload(
                `/v2/survey/${surveyId}/recipient/list`,
                "POST",
                "csv_file",
                selectedFile,
            );
        } catch (err) {
            const serverError =
                fileResponse || "unknown issue in uploading CSV.";
            toast.update(csvSubmitToast, {
                render: `Error uploading CSV, ${serverError}.`,
                type: "error",
                isLoading: false,
                autoClose: 5000,
                pauseOnFocusLoss: false,
                pauseOnHover: false,
                closeOnClick: true,
                closeButton: true,
            });
            reportError(err, { fileResponseErrors: fileResponse?.errors });
        }
        if (fileResponse) {
            toast.update(csvSubmitToast, {
                render: "CSV Uploaded",
                type: "success",
                isLoading: false,
                autoClose: 5000,
                pauseOnFocusLoss: false,
                pauseOnHover: false,
                closeOnClick: true,
                closeButton: true,
            });
            mutate(`/v2/survey/${surveyId}/recipient/list`);
            recipientsMutate();
        } else {
            const serverError =
                fileResponse || "unknown issue in uploading CSV.";
            toast.update(csvSubmitToast, {
                render: `Error uploading Image, ${serverError}.`,
                type: "error",
                isLoading: false,
                autoClose: 5000,
                pauseOnFocusLoss: false,
                pauseOnHover: false,
                closeOnClick: true,
                closeButton: true,
            });
            console.log(fileResponse.errors);
        }
    };

    const [open, setOpen] = useState(false);

    const handleOpen = () => setOpen((open) => !open);
    const notify = () =>
        toast.warn(
            <div>
                <p className="font-bold text-base">Warning</p>
                <p className="text-sm">
                    This link will not be live until after the survey has been
                    launched. Copy the link to create a QR code for printed
                    materials in preparation for launching the survey!
                </p>
            </div>,
            {
                position: "bottom-center",
                autoClose: 7000,
                hideProgressBar: false,
                newestOnTop: false,
                loseOnClick: true,
                rtl: false,
                pauseOnFocusLoss: true,
                draggable: true,
                pauseOnHover: true,
                theme: "light",
            },
        );
    return (
        <>
            {!!surveyRecipients && (
                <>
                    <div className="mt-4">
                        <Card className="card rounded-2xl p-4 bg-platinumlight">
                            {/* Card Header */}
                            <CardHeader
                                floated={false}
                                shadow={false}
                                className="card-inner-header text-xl font-display bg-transparent"
                            >
                                Survey Invitation - Single Link (Open
                                Responses)
                            </CardHeader>

                            {/* Card Body */}
                            <CardBody className="card-inner rounded-xl">
                                {/* Anonymous toggle */}
                                <div className="flex flex-row gap-4 justify-items-center mb-4">
                                    <Typography
                                        as="span"
                                        className="font-display text-primary-text"
                                    >
                                        Allow Open Respondents
                                    </Typography>
                                    <FormikSwitch name="is_allow_anonymous" />
                                </div>

                                {/* Info / Warning Text */}
                                <div className="font-body text-primary-text flex flex-col gap-4">
                                    <Typography>
                                        Create a single link for inviting
                                        people to complete this survey.
                                        <br />
                                        This link will allow open access to
                                        the survey once it has been
                                        launched.
                                    </Typography>
                                    {survey?.state?.trim().toLowerCase() ===
                                        "draft" && (
                                        <div className="text-red-900">
                                            <Typography className="font-bold text-base">
                                                Warning
                                            </Typography>
                                            <Typography className="text-sm">
                                                This link will not be live
                                                until after the survey has
                                                been launched. Copy the link
                                                to create a QR code for
                                                printed materials in
                                                preparation for launching
                                                the survey!
                                            </Typography>
                                        </div>
                                    )}
                                    {survey?.state?.trim().toLowerCase() ===
                                        "draft" && (
                                        <div className="flex justify-center mb-4">
                                            <Button
                                                type="button"
                                                variant="filled"
                                                color="black"
                                                className="btn btn--primary font-display text-center"
                                                onClick={() => {
                                                    notify();
                                                    navigator.clipboard.writeText(
                                                        window.location
                                                            .origin +
                                                            `/respond/${survey.nanoid}`,
                                                    );
                                                }}
                                            >
                                                Copy URL
                                            </Button>
                                        </div>
                                    )}
                                </div>
                                <Typography
                                    variant="paragraph"
                                    className="text-primary-text"
                                >
                                    Use this option where you want to openly
                                    share a link to the survey via various
                                    digital channels, i.e., newsletters,
                                    websites, social posts, emailing to
                                    colleagues to distribute further.
                                </Typography>
                            </CardBody>
                        </Card>
                        <Card className="card rounded-2xl p-4 bg-platinumlight mt-4" shadow={false}>
                            {/* Card Header */}
                            <CardHeader
                                floated={false}
                                shadow={false}
                                className="card-inner-header text-xl font-display bg-transparent"
                            >
                                Survey Invitation - Bespoke Links (Known
                                Recipients)
                            </CardHeader>

                            {/* Card Body */}
                            <CardBody className="card-inner rounded-xl">
                                {/* Single Recipient Upload */}
                                <Typography
                                    variant="h5"
                                    className="font-display text-primary-text mb-2"
                                >
                                    Upload Individual Recipient IDs
                                </Typography>
                                <Typography
                                    variant="paragraph"
                                    className="font-display text-primary-text mb-2"
                                >
                                    Upload Individual Recipient IDs, with an individual email address or other unique
                                    recipient ID
                                </Typography>
                                <div className=" flex flex-row font-body gap-2 items-center md:">
                                    <div className="grow py-4 rounded-xl">
                                        <Input
                                            inputRef={inputTextRef}
                                            icon={
                                                <MaterialIcon name="person_add" />
                                            }
                                            label="Email or ID"
                                            className="bg-white"
                                        />
                                    </div>
                                    <div className="basis-2">
                                        <Button
                                            className="btn btn--primary font-display text-center"
                                            onClick={() =>
                                                addRecipient(
                                                    validationSchemaRecipientEmail,
                                                )
                                            }
                                        >
                                            Add
                                        </Button>
                                    </div>
                                </div>

                                <Typography
                                    variant="paragraph"
                                    className="font-body card-inner p-2 rounded-xl text-primary-text"
                                >
                                    Add an individual email address or other unique
                                    recipient ID. Having an &lsquo;@&rsquo; in the ID text
                                    will trigger email validation.
                                    <br />
                                    Number Of Recipient Invitation Links:{" "}
                                    {(surveyRecipients?.length ?? 0) > 0 ? surveyRecipients.length : "None"}
                                </Typography>

                                {/* Manage Recipient Invitation Links */}
                                <div className="flex flex-wrap justify-end gap-2">
                                    <Button
                                        onClick={handleCsvUploadClick}
                                        className="btn btn--primary font-display"
                                    >
                                        Upload Recipient ID CSV
                                    </Button>
                                    <input
                                        onChange={csvUploadHandler}
                                        ref={inputCsvFileRef}
                                        type="file"
                                        accept=".csv"
                                        style={{ display: "none" }}
                                    />
                                    <Button
                                        onClick={createInvitationCSV}
                                        className="btn btn--primary font-display"
                                    >
                                        Download Invite CSV
                                    </Button>
                                    <Button
                                        onClick={handleOpen}
                                        className="btn btn--primary font-display"
                                    >
                                        View Recipients
                                    </Button>
                                </div>
                            </CardBody>

                            {/* Card Footer */}
                            <CardFooter className="card-inner-footer py-2">
                                <Typography
                                    variant="paragraph"
                                    className="font-display text-primary-text"
                                >
                                    1. Upload a list of Recipient IDs via a
                                    CSV. file only (see image example).
                                    <br />
                                    2. Uploading Recipient IDs (unique
                                    identifiers, such as an email address)
                                    will create an invitation link specific
                                    to them.
                                </Typography>
                            </CardFooter>
                        </Card>
                    </div>
                </>
            )}
            {!!surveyRecipientsError && (
                <Typography>Error loading survey recipients</Typography>
            )}
            {!!surveyRecipients && (
                <>
                    <Dialog open={open} handler={handleOpen} className="dialog">
                        <DialogHeader className="dialog__header">
                            <Typography variant="h2">
                                Recipients List
                            </Typography>
                        </DialogHeader>

                        <DialogBody className="dialog__body">
                            <Typography className="font-body text-primary-text mb-4">
                                There{" "}
                                {surveyRecipients.length !== 1 ? "are" : "is"}{" "}
                                currently&nbsp;
                                {surveyRecipients.length} named recipient
                                {surveyRecipients.length !== 1 ? "s" : ""} for
                                this survey:
                            </Typography>

                            <ul className="space-y-2">
                                {surveyRecipients.map(({ id, uid }) => (
                                    <li
                                        key={uid}
                                        className="flex items-center justify-between bg-platinumlight p-2 rounded-xl"
                                    >
                                        <span className="font-body text-primary-text">
                                            {uid}
                                        </span>
                                        <div className="flex gap-2">
                                            <IconButton
                                                className="btn btn--primary p-2"
                                                color="white"
                                                type="button"
                                                variant="text"
                                                size="sm"
                                                onClick={() =>
                                                    createRecipientLink(id)
                                                }
                                            >
                                                <MaterialIcon name="share" />
                                            </IconButton>
                                            <IconButton
                                                className="btn btn--danger p-2"
                                                color="white"
                                                type="button"
                                                variant="text"
                                                size="sm"
                                                onClick={() =>
                                                    deleteRecipient(id)
                                                }
                                            >
                                                <MaterialIcon name="delete" />
                                            </IconButton>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </DialogBody>

                        <DialogFooter className="dialog__footer">
                            <Button
                                type="button"
                                className="btn btn--primary"
                                onClick={handleOpen}
                            >
                                Close
                            </Button>
                        </DialogFooter>
                    </Dialog>
                </>
            )}
        </>
    );
}
